import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { dateToLabel } from './dates';
import { CalendarDayInterface } from 'further-types/utils';

class CalendarDay implements CalendarDayInterface {
  public readonly date: string;

  public constructor(calendarDay: string | Date) {
    if (calendarDay instanceof Date) {
      this.date = format(calendarDay, 'yyyy-MM-dd');
    } else {
      this.date = calendarDay;
    }
  }

  public static getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public toString() {
    return this.date;
  }

  toJSON() {
    return {
      class: 'CalendarDay',
      date: this.date,
    };
  }

  public toLabel(fallback = 'N/A', format?: string) {
    return dateToLabel(this.date, fallback, false, false, format);
  }

  public toReadable(useFullMonthName = false) {
    return this.toLabel(
      '--',
      useFullMonthName ? 'MMMM d, yyyy' : 'MMM d, yyyy',
    );
  }

  public endOfDay() {
    const date = toDate(this.date, {
      timeZone: 'UTC',
    });

    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
        999,
      ),
    );
  }

  public startOfDay() {
    return this.toUTCDate();
  }

  public toUTCDate() {
    const date = toDate(this.date, {
      timeZone: 'UTC',
    });

    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
    );
  }

  public toLocalDateLabel() {
    const browserTimezone = CalendarDay.getBrowserTimezone();
    const date = toDate(this.date, {
      timeZone: browserTimezone,
    });
    return format(date, 'yyyy-MM-dd');
  }

  public toLocalDate() {
    const browserTimezone = CalendarDay.getBrowserTimezone();
    const date = toDate(this.date, {
      timeZone: browserTimezone,
    });
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    );
  }

  public isAfter(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isAfter(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isBefore(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isBefore(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isSameDay(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isSameDay(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isSameOrAfter(date: CalendarDay | Date) {
    return this.isAfter(date) || this.isSameDay(date);
  }

  public isSameOrBefore(date: CalendarDay | Date) {
    return this.isBefore(date) || this.isSameDay(date);
  }

  public isInFuture() {
    return this.isAfter(new CalendarDay(new Date()));
  }
}

export default CalendarDay;
